import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import { PaginationItem } from "./PaginationItem";
import { PaginationOverflowMenu } from "./PaginationOverflowMenu";
import { PaginationItemsPerPageMenu } from "./PaginationItemsPerPageMenu";
import { PaginationList } from "./PaginationList";
import { PaginationTotalCount } from "./PaginationTotalCount";

import styles from "./Pagination.module.scss";

/**
 * Props for the Pagination component
 * @extends ComponentPropsWithoutRef<"nav">
 */
export type PaginationProps = ComponentPropsWithoutRef<"nav">;

/**
 * Pagination component for navigating through paginated content.
 *
 * Features:
 * - Provides navigation controls for paginated data
 * - Supports compound components for flexible pagination layouts
 * - Includes list, items, overflow menu, items per page menu, and total count
 * - Accessible navigation with proper ARIA roles
 * - Customizable styling and layout
 *
 * @example
 * <Pagination>
 *   <Pagination.List>
 *     <Pagination.Item>1</Pagination.Item>
 *     <Pagination.Item>2</Pagination.Item>
 *     <Pagination.OverflowMenu />
 *     <Pagination.Item>10</Pagination.Item>
 *   </Pagination.List>
 *   <Pagination.ItemsPerPageMenu />
 *   <Pagination.TotalCount />
 * </Pagination>
 */
export const Pagination = Object.assign(
  forwardRef<HTMLElement, PaginationProps>(
    function PaginationInner(props, ref) {
      const { className, children, ...rest } = props;

      const paginationClassName = cx(styles.pagination, className);

      return (
        <nav ref={ref} className={paginationClassName} {...rest}>
          {children}
        </nav>
      );
    },
  ),
  {
    List: PaginationList,
    Item: PaginationItem,
    OverflowMenu: PaginationOverflowMenu,
    ItemsPerPageMenu: PaginationItemsPerPageMenu,
    TotalCount: PaginationTotalCount,
  },
);
Pagination.displayName = "Pagination";
